import { GraduationCap, Handshake, Megaphone, Presentation, Rocket, Sprout, Store, Tractor, type LucideIcon } from 'lucide-react';
import { PALETTE, TAG_TONE_HUE } from '../../lib/palette';
import type { TagTone } from '../../ui';

/**
 * WHAT A MEET IS, AS A WORD, A COLOUR AND A GLYPH — one definition for the whole events area.
 *
 * There were THREE copies of this: the list's labels, the detail header's labels, and the form's own
 * colour-and-glyph set — which put a retailer meet in sky on the form and grey everywhere else. A
 * type that is one colour where you pick it and another where you read it is a colour that has
 * stopped meaning anything, so the list, the detail header, the Type filter and the form all read
 * from here.
 *
 * The glyph VARIES with the type (a sprout for a farmer meet, a shopfront for a dealer meet, a
 * mortarboard for training): an identity mark that is the same picture on every event marks nothing.
 *
 * `chip` (bordered pill) and `tint` (fill only, for a square swatch) are whole literal class strings,
 * because Tailwind only emits classes it can SEE in the source; a built-up `bg-${x}-500/10` compiles
 * to nothing. `hue` is that same colour as a REAL value, for what cannot take a class — the dot on a
 * filter option, the tint a set filter control wears.
 *
 * NO type is sky or harvest. Those two belong to the WHEN and the WHERE on the detail header, which
 * sit right beside the type chip; a product launch in the same gold as its own venue made two
 * different facts look like one.
 */
export interface EventTypeMeta { label: string; hue: string; chip: string; tint: string; Icon: LucideIcon }

export const TYPE_META: Record<string, EventTypeMeta> = {
  RETAILER_MEET: {
    label: 'Retailer meet', hue: '#06b6d4', Icon: Store,
    chip: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
    tint: 'bg-cyan-500/12 text-cyan-700 dark:text-cyan-300',
  },
  FARMER_MEET: {
    label: 'Farmer meet', hue: TAG_TONE_HUE.green, Icon: Sprout,
    chip: 'border-brand-500/30 bg-brand-500/10 text-brand-700 dark:text-brand-300',
    tint: 'bg-brand-500/14 text-brand-700 dark:text-brand-300',
  },
  TRAINING: {
    label: 'Training', hue: '#8b5cf6', Icon: GraduationCap,
    chip: 'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300',
    tint: 'bg-violet-500/12 text-violet-700 dark:text-violet-300',
  },
  SEMINAR: {
    label: 'Seminar', hue: '#6366f1', Icon: Presentation,
    chip: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
    tint: 'bg-indigo-500/12 text-indigo-700 dark:text-indigo-300',
  },
  PRODUCT_LAUNCH: {
    label: 'Product launch', hue: '#c026d3', Icon: Rocket,
    chip: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300',
    tint: 'bg-fuchsia-500/12 text-fuchsia-700 dark:text-fuchsia-300',
  },
  FIELD_DAY: {
    label: 'Field day', hue: PALETTE.earth[500], Icon: Tractor,
    chip: 'border-earth-500/30 bg-earth-500/10 text-earth-600 dark:text-earth-400',
    tint: 'bg-earth-500/12 text-earth-600 dark:text-earth-400',
  },
  MEETING: {
    label: 'Meeting', hue: '#f43f5e', Icon: Handshake,
    chip: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
    tint: 'bg-rose-500/12 text-rose-700 dark:text-rose-300',
  },
  OTHER: {
    label: 'Other', hue: '#64748b', Icon: Megaphone,
    chip: 'border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300',
    tint: 'bg-slate-500/12 text-slate-700 dark:text-slate-300',
  },
};

/** The types, in the order the app offers them. */
export const EVENT_TYPES = Object.keys(TYPE_META);

/** Just the words, for the places that only print a label. */
export const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(TYPE_META).map(([k, v]) => [k, v.label]),
);

/** An unknown type still gets a readable label and a colourless mark, never a blank. */
export const typeMeta = (type?: string | null): EventTypeMeta =>
  TYPE_META[String(type ?? '')] ?? { ...TYPE_META.OTHER, label: String(type ?? 'Other') };

/**
 * WHERE A MEET IS IN ITS LIFE. The tone is the one the status tag wears in the table and on the
 * header, so a filter set to "Completed" and the green rows it produces are one colour, not two.
 */
export const STATUS_TONE: Record<string, TagTone> = {
  PLANNED: 'blue', ONGOING: 'gold', COMPLETED: 'green', CANCELLED: 'red',
};
export const EVENT_STATUSES = Object.keys(STATUS_TONE);
export const statusLabel = (s?: string | null) => (s ?? '').replace(/_/g, ' ');
/** The status' colour as a value — for a filter dot, which cannot take a class. */
export const statusHue = (s?: string | null) => TAG_TONE_HUE[STATUS_TONE[String(s ?? '')] ?? 'neutral'];
